import { memo } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { NotificationChannel } from '@masters-club/types';
import { FormField } from '@/components/forms';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import { BotSelect } from '@/components/shared/BotSelect';

type LanguageRecord = {
  code: string;
  name: string;
  nativeName: string;
};

interface TabScheduleProps {
  scheduledAt: string;
  setScheduledAt: (value: string) => void;
  isPending: boolean;
  // Translation warning
  languages: LanguageRecord[];
  translations: Map<string, string>;
  messageTemplate: string;
  // Бот для отправки TELEGRAM-рассылки
  botId: number | null;
  setBotId: (value: number | null) => void;
  channels: NotificationChannel[];
}

export const TabSchedule = memo(function TabSchedule({
  scheduledAt,
  setScheduledAt,
  isPending,
  languages,
  translations,
  messageTemplate,
  botId,
  setBotId,
  channels,
}: TabScheduleProps) {
  const dateValue = scheduledAt ? new Date(scheduledAt) : null;

  const handleDateChange = (date: Date | null) => {
    if (!date) {
      setScheduledAt('');
      return;
    }
    setScheduledAt(date.toISOString());
  };

  const missingLangs = languages.filter(
    (lang) => !translations.has(lang.code) || !translations.get(lang.code)?.trim(),
  );

  const hasTelegramChannel = channels.includes('TELEGRAM');

  return (
    <div className="space-y-6">
      <FormField
        label="Запланировать отправку"
        name="scheduledAt"
        hint="Оставьте пустым, чтобы отправить сразу после создания"
      >
        <DateTimePicker
          value={dateValue}
          onChange={handleDateChange}
          placeholder="Выберите дату и время отправки"
          disabled={isPending}
          disablePast
        />
      </FormField>

      {hasTelegramChannel && (
        <FormField
          label="Бот"
          name="botId"
          hint="Через какого Telegram-бота отправлять сообщения и подставлять {{botUsername}} в шаблон. «По умолчанию» = первый активный бот."
        >
          <BotSelect value={botId} onChange={setBotId} disabled={isPending} />
        </FormField>
      )}

      {/* Missing translations warning */}
      {languages.length > 0 && missingLangs.length > 0 && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-950">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                Нет переводов для некоторых языков
              </p>
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                Пользователи с языками{' '}
                <span className="font-medium">
                  {missingLangs.map((l) => l.nativeName || l.name).join(', ')}
                </span>
                {' '}получат сообщение из основного шаблона{messageTemplate ? '.' : ' (основной шаблон пуст!).'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
